/* eslint-disable max-len */
/* eslint-disable no-alert */
/**
 * @typedef {Object} RadialMenuOptions
 * @property {string} [fontFamily=FontAwesome] Name of the font to be used. On this example 'FontAwesome' is being used for the free icons. But you can use your own font.
 * @property {number} [fontSize=14] Size of the icons (text) used on the buttons.
 * @property {number} [innerCircle=50] Inner circle of the radial menu. Use 0 (zero) if you don't want a hole in the menu.
 * @property {number} [outerCircle=100] Outer circle of the radial menu. The outer circle and the inner circle will defined how thick is the menu.
 * @property {number} [rotation=PI/2] This value rotate the whole "circle" of the menu, if you want to better "align" the button's divison. *This value is in radians and always rotate the menu clock wise*.
 * @property {number} [shadowBlur=10] How blurred is the shadow.
 * @property {DOMString|GradientObject} [shadowColor=rgba(0,0,0,0.2)] Shadow color.
 * @property {number} [shadowOffsetX=3] Horizontal displacement of the shadow.
 * @property {number} [shadowOffsetY=3] Vertical displacement of the shadow.
 * @property {DOMString|GradientObject} [backgroundColor=#EEE] The background color of the button.
 * @property {DOMString|GradientObject} [hoverBackgroundColor] The hover background color of the button.
 * @property {DOMString|GradientObject} [borderColor=#FFF] The border color of the button.
 * @property {DOMString|GradientObject} [textColor=#000] Color of the text inside the button.
 * @property {DOMString|GradientObject} [hoverTextColor] Color of the text inside the button when hover.
 * @property {DOMString|GradientObject} [textBorderColor=transparent] Color of the contour of the text inside the button.
 * @property {DOMString|GradientObject} [textShadowColor=transparent] Color of the shadow of the text.
 * @property {number} [textShadowBlur=0] How blurred is the shadow of the text.
 * @property {number} [textShadowOffsetX=0] Horizontal displacement of the shadow of the text.
 * @property {number} [textShadowOffsetY=0] Vertical displacement of the shadow of the text.
 * @property {number} [buttonGap=0] Gap between buttons. *This value is in radians*.
 * @property {Array<RadialMenuButton>} [buttons] The menu button list.
 * @property {function(boolean):void} [hoverAction] Callback when hover state changes. When menu is hovered, true argument is set when fucntion call, false otherwise.
 * @property {number} [posX=0] Horizontal position of the menu. This value is used only when the menu is fixed on the page.
 * @property {number} [posY=0] Vertical position of the menu. This value is used only when the menu is fixed on the page.
 * @property {boolean} [isFixed=false] This value determine if the menu will be fixed on the page. This is usefull in case you're making a web app that needs a menu that is always visible.
 * @property {number} [zIndex=9999] This value determine the order the menu will be displayed on the page. Higher values means that it is in front of elements with lower values.
 */

/**
 * @typedef {Object} RadialMenuButton The options to draw menu button.
 * @property {DOMString|GradientObject} [backgroundColor=#EEE] The background color of the button.
 * @property {DOMString|GradientObject} [borderColor=#FFF] The border color of the button.
 * @property {DOMString|GradientObject} [textColor=#000] Color of the text inside the button.
 * @property {DOMString|GradientObject} [textBorderColor=transparent] Color of the contour of the text inside the button.
 * @property {DOMString|GradientObject} [textShadowColor=transparent] Color of the shadow of the text.
 * @property {number} [textShadowBlur=0] How blurred is the shadow of the text.
 * @property {number} [textShadowOffsetX=0] Horizontal displacement of the shadow of the text.
 * @property {number} [textShadowOffsetY=0] Vertical displacement of the shadow of the text.
 * @property {number} [buttonGap=0] Gap between buttons. *This value is in radians*.
 * @property {function(MouseEvent):void} action is a function that will be called when the button is clicked.
 * @property {string} text is the icon that will be displayed. [see the font-visualizer.html for the unicode of each icon *'\uf000'*](https://victorribeiro.com/radialMenu/font-visualizer.html).
 */

/**
 * @typedef {Object} ShadowStyle The style to render a shadow in the canvas
 * @property {DOMString|GradientObject} color The shadow color.
 * @property {number} blur The shadow blur.
 * @property {number} offsetX The shadow X offset.
 * @property {number} offsetY The shadow Y offset.
 */

/**
 * @typedef {Object} GradientObject The gradient object color.
 * @property {string} gradient The gradient type :
 * radial - *from inside to outside*
 * linear1 - *top to bottom*
 * linear2 - *left to right*
 * linear3 - *top left to bottom right*
 * linear4 - *bottom left to top right*
 * @property {Array<DOMColor>} colors An array which contains initial et final gradient color.
 */

/** @type {ShadowStyle} */
const NO_SHADOW = { color: 'transparent', blur: 0 };

/** @type {number} */
const TWOPI = 2 * Math.PI;

export default class RadialMenu {
  /**
   * @constructor
   * @param {RadialMenuOptions} options The radial menu options.
   */
  constructor({
    fontFamily, fontSize, innerCircle, outerCircle,
    rotation, shadowBlur, shadowColor, shadowOffsetX,
    shadowOffsetY, backgroundColor, hoverBackgroundColor, borderColor, textColor,
    hoverTextColor, textBorderColor, textShadowColor, textShadowBlur,
    textShadowOffsetX, textShadowOffsetY, buttonGap, buttons, hoverAction,
    posX, posY, isFixed, zIndex,
  } = {}) {
    this.state = {};

    this.state.hover = undefined;

    this.hoverAction = hoverAction || (() => {});

    this.scale = window.devicePixelRatio;

    this.fontFamily = fontFamily || 'FontAwesome';

    this.fontSize = fontSize || 14;

    if (Number.isNaN(this.fontSize)) throw new Error('Font size must be a number');

    this.innerCircle = !Number.isNaN(innerCircle) ? innerCircle : 50;

    this.outerCircle = !Number.isNaN(outerCircle) ? outerCircle : 100;

    if (this.innerCircle > this.outerCircle) throw new Error('Inner circle can\'t be larger than outer circle');

    if (this.innerCircle < 0) throw new Error('Inner circle can\'t be negative');

    this.rotation = rotation % TWOPI || 0;
    while (this.rotation < 0) {
      // eslint-disable-next-line no-param-reassign
      rotation += TWOPI;
    }

    this.shadowStyle = {

      color: shadowColor || 'rgba(0,0,0,0.5)',

      blur: !Number.isNaN(shadowBlur) ? shadowBlur : 10,

      offsetX: !Number.isNaN(shadowOffsetX) ? shadowOffsetX : 3,

      offsetY: !Number.isNaN(shadowOffsetY) ? shadowOffsetY : 3,

    };

    this.backgroundColor = backgroundColor || '#EEE';

    this.hoverBackgroundColor = hoverBackgroundColor;

    this.borderColor = borderColor || '#FFF';

    this.buttonGap = buttonGap || 0;

    this.textColor = textColor || '#000';

    this.hoverTextColor = hoverTextColor;

    this.textBorderColor = textBorderColor || 'transparent';

    this.textShadowColor = textShadowColor || 'transparent';

    this.textShadowBlur = !Number.isNaN(textShadowBlur) ? textShadowBlur : 10;

    this.textShadowOffsetX = !Number.isNaN(textShadowOffsetX) ? textShadowOffsetX : 3;

    this.textShadowOffsetY = !Number.isNaN(textShadowOffsetY) ? textShadowOffsetY : 3;

    this.posX = posX || 0;

    this.posY = posY || 0;

    this.isFixed = isFixed || false;

    this.zIndex = zIndex || 9999;

    this.canvas = document.createElement('canvas');

    this.addButtons(buttons || [

      { text: '\ue813', action: () => { alert(1); } },

      { text: '\ue814', action: () => { alert(2); } },

      { text: '\ue815', action: () => { alert(3); } },

    ]);

    this.addEvent();

    this.draw();
  }

  init() {
    this.w = (this.outerCircle * 2) + (this.shadowStyle.blur * 2) + (this.shadowStyle.offsetX * 2);

    this.h = (this.outerCircle * 2) + (this.shadowStyle.blur * 2) + (this.shadowStyle.offsetY * 2);

    this.canvas.style.display = 'none';

    this.canvas.style.position = 'fixed';

    this.canvas.style.width = `${this.w}px`;

    this.canvas.style.height = `${this.h}px`;

    this.canvas.width = this.w * this.scale;

    this.canvas.height = this.h * this.scale;

    this.c = this.canvas.getContext('2d');

    this.c.scale(this.scale, this.scale);

    this.c.font = `${this.fontSize}px ${this.fontFamily}`;

    this.canvas.style.zIndex = this.zIndex;

    // eslint-disable-next-line no-bitwise
    this.w2 = this.w >> 1;

    // eslint-disable-next-line no-bitwise
    this.h2 = this.h >> 1;

    if (this.backgroundColor instanceof Object) this.backgroundColor = this.createGradient(this.backgroundColor);

    if (this.borderColor instanceof Object) this.borderColor = this.createGradient(this.borderColor);

    if (this.shadowStyle.color instanceof Object) this.shadowStyle.color = this.createGradient(this.shadowStyle.color);

    if (this.textColor instanceof Object) this.textColor = this.createGradient(this.textColor);

    if (this.textBorderColor instanceof Object) this.textBorderColor = this.createGradient(this.textBorderColor);

    if (this.textShadowColor instanceof Object) this.textShadowColor = this.createGradient(this.textShadowColor);

    const margin = this.buttonGap;

    const innerMargin = 2 * Math.atan((this.outerCircle / this.innerCircle) * Math.tan(this.buttonGap / 2));

    const { buttons } = this;

    this.step = buttons.length > 1 ? TWOPI / buttons.length : TWOPI / 3;

    const rotation = (buttons.length > 1 ? this.rotation : (7 / 6) * Math.PI) % TWOPI;

    for (let i = 0; i < buttons.length; i++) {
      buttons[i].ini = (i * this.step + rotation + margin) % TWOPI;

      buttons[i].fin = (buttons[i].ini + this.step - 2 * margin) % TWOPI;

      buttons[i].ini_inner = (i * this.step + rotation + innerMargin) % TWOPI;

      buttons[i].fin_inner = (buttons[i].ini_inner + this.step - 2 * innerMargin) % TWOPI;

      if (buttons[i].ini > buttons[i].fin) {
        this.rest = i;
      }

      const a = (buttons[i].ini + (this.step) / 2 - margin);

      buttons[i].centerX = Math.cos(a) * (this.innerCircle + (this.outerCircle - this.innerCircle) / 2);

      buttons[i].centerY = Math.sin(a) * (this.innerCircle + (this.outerCircle - this.innerCircle) / 2) + this.fontSize / 4;
    }
  }

  draw() {
    const ctx = this.c;

    ctx.imageSmoothingEnabled = true;

    ctx.imageSmoothingQuality = 'high';

    ctx.clearRect(0, 0, this.w, this.h);

    if (!this.buttonGap) {
      setContextShadowStyle(ctx, this.shadowStyle);

      ctx.fillStyle = this.shadowStyle.color;

      ctx.beginPath();

      ctx.arc(this.w2, this.h2, this.outerCircle, 0, TWOPI);

      ctx.arc(this.w2, this.h2, this.innerCircle, TWOPI, 0, true);

      ctx.fill();
    }

    for (let i = 0; i < this.buttons.length; i++) {
      const button = this.buttons[i];

      if (this.buttonGap) {
        setContextShadowStyle(ctx, this.shadowStyle);
      } else {
        setContextShadowStyle(ctx, NO_SHADOW);
      }

      if (button.disabled) {
        ctx.fillStyle = `${'backgroundColor' in button ? button.backgroundColor : this.backgroundColor}33`;
      } else if (this.hoverBackgroundColor && this.state.hover === button) {
        ctx.fillStyle = this.hoverBackgroundColor;
      } else {
        ctx.fillStyle = 'backgroundColor' in button ? button.backgroundColor : this.backgroundColor;
      }

      ctx.strokeStyle = 'borderColor' in button ? button.borderColor : this.borderColor;

      ctx.beginPath();

      ctx.arc(this.w2, this.h2, this.outerCircle, button.ini, button.fin);

      ctx.arc(this.w2, this.h2, this.innerCircle, button.fin_inner, button.ini_inner, true);

      ctx.closePath();

      ctx.fill();

      if (this.buttonGap) {
        setContextShadowStyle(ctx, NO_SHADOW);
      }

      ctx.stroke();

      setContextShadowStyle(ctx, {

        color: 'textShadowColor' in button ? button.textShadowColor : this.textShadowColor,

        blur: 'textShadowBlur' in button ? button.textShadowBlur : this.textShadowBlur,

        offsetX: 'textShadowOffsetX' in button ? button.textShadowOffsetX : this.textShadowOffsetX,

        offsetY: 'textShadowOffsetY' in button ? button.textShadowOffsetY : this.textShadowOffsetY,

      });

      if (this.hoverTextColor && this.state.hover === button) {
        ctx.fillStyle = this.hoverTextColor;
      } else {
        ctx.fillStyle = 'textColor' in button ? button.textColor : this.textColor;
      }

      ctx.strokeStyle = 'textBorderColor' in button ? button.textBorderColor : this.textBorderColor;

      ctx.save();

      ctx.translate(this.w2, this.h2);

      const deltaX = ctx.measureText(button.text).width / 2 || this.fontSize / 2;

      ctx.fillText(button.text, button.centerX - deltaX, button.centerY);

      ctx.strokeText(button.text, button.centerX - deltaX, button.centerY);

      ctx.restore();
    }
  }

  addEvent() {
    this.canvas.addEventListener('click', (event) => {
      const clickedButton = this.getButton(event.clientX, event.clientY);

      if (clickedButton && !clickedButton.disabled) {
        clickedButton.action(event);
      }
    });

    if (this.hoverBackgroundColor || this.hoverTextColor || this.hoverAction) {
      this.canvas.addEventListener('mousemove', (e) => {
        const previousHoverState = this.state.hover;

        this.state.hover = this.getButton(e.clientX, e.clientY);

        if (previousHoverState !== this.state.hover) {
          this.hoverAction(this.state.hover !== undefined);

          this.draw();
        }
      });
    }

    if (this.isFixed) {
      this.setPos(this.posX, this.posY);

      this.show();
    }
  }

  // eslint-disable-next-line class-methods-use-this
  distance(x1, y1, x2, y2) {
    return Math.sqrt(((x2 - x1) ** 2) + ((y2 - y1) ** 2));
  }

  hide() {
    this.canvas.style.display = 'none';
  }

  show() {
    this.draw();

    this.canvas.style.display = 'block';
  }

  setPos(x, y) {
    if (Number.isNaN(x) || Number.isNaN(y)) throw new Error('X and Y must be numbers');

    this.canvas.style.left = `${x}px`;

    this.canvas.style.top = `${y}px`;
  }

  createGradient(obj) {
    if (!('gradient' in obj)) throw new Error('Invalid gradient object');

    let gradient;

    switch (obj.gradient) {
      case 'radial':

        gradient = this.c.createRadialGradient(this.w2, this.h2, this.innerCircle, this.w2, this.h2, this.outerCircle);

        break;

      case 'linear1':

        gradient = this.c.createLinearGradient(0, 0, 0, this.canvas.height);

        break;

      case 'linear2':

        gradient = this.c.createLinearGradient(0, 0, this.canvas.width, 0);

        break;

      case 'linear3':

        gradient = this.c.createLinearGradient(0, 0, this.canvas.width, this.canvas.height);

        break;

      case 'linear4':

        gradient = this.c.createLinearGradient(0, this.canvas.height, this.canvas.width, 0);

        break;

      default:

        throw new Error('Invalid gradient value');
    }

    for (const key of Object.keys(obj.colors)) {
      if (Number.isNaN(key) || key < 0 || key > 1) throw new Error('Invalid color position');

      gradient.addColorStop(key, obj.colors[key]);
    }

    return gradient;
  }

  checkButtons() {
    if (!(this.buttons instanceof Array)) throw new Error('Buttons must be an Array of button objects');

    for (let i = 0; i < this.buttons.length; i++) {
      const button = this.buttons[i];

      if (!('text' in button) || !('action' in button)) throw new Error('Button must have a text and an action value');
    }
  }

  addButtons(buttons) {
    this.buttons = buttons;

    this.rest = -1;

    this.checkButtons();

    this.init();
  }

  /**
   * Get the button concerne by the event
   * @param {number} xPixel The x coordinate of the pixel mouse event.
   * @param {number} yPixel The y coordinate of the pixel mouse event.
   * @return {any} The button concerne by the event or undefined if no button.
   */
  getButton(xPixel, yPixel) {
    const rect = this.canvas.getBoundingClientRect();

    const _x = this.w / rect.width;

    const _y = this.h / rect.height;

    const posX = (rect.left + this.w2) * _x;

    const posY = (rect.top + this.h2) * _y;

    const d = this.distance(xPixel, yPixel, posX, posY);

    const { buttons } = this;

    let theButton;

    if (d > this.innerCircle && d < this.outerCircle) {
      let a = Math.atan2(yPixel - posY, xPixel - posX);

      a = a > 0 ? a : TWOPI + a;

      for (let i = 0; i < buttons.length; i++) {
        if (a >= buttons[i].ini && a <= buttons[i].fin) {
          theButton = buttons[i];

          break;
        }
      }

      if (!theButton && this.rest !== -1) {
        if (a < buttons[this.rest].ini) {
          a += TWOPI;
        }

        if (a >= buttons[this.rest].ini && a <= buttons[this.rest].fin + TWOPI) {
          theButton = buttons[this.rest];
        }
      }
    }

    return theButton;
  }
}

/**
 * Set styles in the canvas rendering context.
 * @param {CanvasRenderingContext2D} context The canvas context.
 * @param {ShadowStyle} shadowStyle The shadow style to set in the context.
 */
function setContextShadowStyle(context, shadowStyle) {
  context.shadowColor = shadowStyle.color;
  context.shadowBlur = shadowStyle.blur;
  context.shadowOffsetX = shadowStyle.offsetX;
  context.shadowOffsetY = shadowStyle.offsetY;
}
